import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import { useAppSelector } from '../../../store/hooks';
import { GridEventListener } from '@mui/x-data-grid';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { provinceApi } from '../../../services';
import { useErrorHandler } from '../../../lib/errorHandler';
import { ProvinceSchema } from '../../../services/provinceApi';
import { z } from 'zod';
import { MOBILE_BREAKPOINT, ROW_HEIGHTS, LIST_HEIGHTS } from '../../../utils/responsive';
import { useEntityListState, createEntityToolbar, type StatsQueryParams } from '../../../hooks';

type Province = z.infer<typeof ProvinceSchema>;
import { BaseDataGrid, PageHeader, DeleteConfirmDialog, ProvinceDialog, PersistentTabs, TabConfig } from '../../../components';
import { ProvincesChart } from './ProvincesChart';

const ProvincesToolbar = createEntityToolbar({
	searchPlaceholder: 'Tìm kiếm tỉnh thành...',
	exportFn: (params) => provinceApi.exportToExcel(params),
});
import { ProvincesChoropleth } from './ProvincesChoropleth';
import { useProvincesColumns, useProvincesQuery } from '../hooks';

interface ProvincesOriginalContentProps {
	isAdmin: boolean;
	canEdit: boolean;
}

export default function ProvincesOriginalContent({ isAdmin, canEdit }: ProvincesOriginalContentProps) {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down(MOBILE_BREAKPOINT));
	const [searchParams, setSearchParams] = useSearchParams();
	const selectedFaculty = useAppSelector((state) => state.filters.selectedFaculty);
	const { showError, showSuccess } = useErrorHandler();
	const queryClient = useQueryClient();

	const {
		searchValue, paginationModel, sortModel, filterModel,
		handlePaginationModelChange, handleSortModelChange,
		handleFilterModelChange, handleSearch,
	} = useEntityListState({ defaultSortField: 'tenTinh' });

	const [provinceDialog, setProvinceDialog] = useState<{
		open: boolean;
		mode: 'create' | 'edit' | 'view';
		province: Province | null;
	}>({ open: false, mode: 'create', province: null });
	const dialogRef = useRef(provinceDialog);
	dialogRef.current = provinceDialog;

	const [deleteDialog, setDeleteDialog] = useState<{
		open: boolean;
		province: Province | null;
	}>({ open: false, province: null });

	const handleCreate = useCallback(() => {
		setSearchParams({ action: 'create' });
	}, [setSearchParams]);

	const handleEdit = useCallback(
		(province: Province) => () => {
			setSearchParams({ action: 'edit', id: province.maTinh });
		},
		[setSearchParams],
	);

	const handleDelete = useCallback(
		(province: Province) => () => {
			setDeleteDialog({
				open: true,
				province,
			});
		},
		[],
	);

	const queryParams: StatsQueryParams = {
		filters: {
			query: searchValue,
			maKhoa: selectedFaculty ? [selectedFaculty] : undefined
		},
		page: paginationModel.page,
		pageSize: paginationModel.pageSize,
		sortBy: sortModel.length > 0 ? sortModel[0].field : undefined,
		sortOrder: (sortModel.length > 0 ? sortModel[0].sort : undefined) as 'asc' | 'desc' | undefined,
	};
	const { data: provincesData, isLoading, refetch } = useProvincesQuery(queryParams);
	const exportParams = useProvincesQuery.buildExportParams(queryParams);

	const provinces = useMemo(() => provincesData?.data?.stats || [], [provincesData?.data?.stats]);
	const totalCount = provincesData?.pagination?.total || 0;

	// Sync URL params with dialog state
	useEffect(() => {
		const action = searchParams.get('action') as 'create' | 'edit' | 'view' | null;
		const provinceId = searchParams.get('id');

		// Security: Redirect unauthorized users from edit/create to view mode
		if (action && ['create', 'edit'].includes(action) && !canEdit) {
			if (provinceId) {
				setSearchParams({ action: 'view', id: provinceId });
			} else {
				setSearchParams({});
			}
			return;
		}

		if (action && ['create', 'edit', 'view'].includes(action)) {
			if (action === 'create') {
				if (!dialogRef.current.open || dialogRef.current.mode !== 'create') {
					setProvinceDialog({
						open: true,
						mode: 'create',
						province: null,
					});
				}
			} else if (provinceId && provinces.length > 0) {
				if (dialogRef.current.open
					&& dialogRef.current.province?.maTinh === provinceId
					&& dialogRef.current.mode === action) {
					return;
				}
				const province = provinces.find((p) => p.maTinh === provinceId);
				if (province) {
					setProvinceDialog({
						open: true,
						mode: action,
						province: province as unknown as Province,
					});
				}
			}
		} else if (!action && dialogRef.current.open) {
			setProvinceDialog({ open: false, mode: 'create', province: null });
		}
	}, [searchParams, provinces, canEdit, setSearchParams]);

	const deleteMutation = useMutation({
		mutationFn: provinceApi.deleteProvince,
		onSuccess: () => {
			showSuccess('Xóa tỉnh/thành phố thành công');
			queryClient.invalidateQueries({ queryKey: ['provinces'] });
			setDeleteDialog({ open: false, province: null });
		},
		onError: (error: Error) => {
			showError(error.message || 'Có lỗi xảy ra khi xóa tỉnh/thành phố');
		},
	});

	const handleRefresh = useCallback(() => {
		if (isLoading) return;
		refetch();
	}, [isLoading, refetch]);

	const handleRowClick: GridEventListener<'rowClick'> = useCallback((params) => {
		setSearchParams({ action: canEdit ? 'edit' : 'view', id: (params.row as Province).maTinh });
	}, [canEdit, setSearchParams]);

	const handleDeleteConfirm = useCallback(() => {
		if (deleteMutation.isPending || !deleteDialog.province) return;
		deleteMutation.mutate(deleteDialog.province.maTinh);
	}, [deleteDialog.province, deleteMutation]);

	const handleCloseProvinceDialog = useCallback(() => {
		setSearchParams({});
	}, [setSearchParams]);

	const handleCloseDeleteDialog = useCallback(() => {
		setDeleteDialog({ open: false, province: null });
	}, []);

	const columns = useProvincesColumns({
		isAdmin,
		isMobile,
		provinceStats: provincesData?.data,
		onEdit: handleEdit,
		onDelete: handleDelete
	});

	const tabs: TabConfig[] = [
		{
			label: 'Danh sách',
			content: (
				<Box sx={{
					flex: 1,
					minHeight: 0,
					height: isMobile ? LIST_HEIGHTS.MOBILE : LIST_HEIGHTS.DESKTOP,
					display: 'flex',
					flexDirection: 'column'
				}}>
					<BaseDataGrid
						rows={provinces}
						columns={columns}
						loading={isLoading}
						onRowClick={handleRowClick}
						getRowId={(row) => row.maTinh}
						rowCount={totalCount}
						paginationModel={paginationModel}
						onPaginationModelChange={handlePaginationModelChange}
						sortModel={sortModel}
						onSortModelChange={handleSortModelChange}
						filterModel={filterModel}
						onFilterModelChange={handleFilterModelChange}
						pageSizeOptions={[10, 25, 50, 100]}
						paginationMode="server"
						sortingMode="server"
						filterMode="server"
						disableRowSelectionOnClick
						rowHeight={isMobile ? undefined : ROW_HEIGHTS.DESKTOP}
						getRowHeight={isMobile ? () => 'auto' : undefined}
						slots={{
							toolbar: () => (
								<ProvincesToolbar
									searchValue={searchValue}
									onSearch={handleSearch}
									onRefresh={handleRefresh}
									exportParams={exportParams}
									isMobile={isMobile}
								/>
							),
						}}
					/>
				</Box>
			)
		},
		{
			label: 'Biểu đồ',
			content: (
				<Box sx={{ height: isMobile ? LIST_HEIGHTS.MOBILE : LIST_HEIGHTS.DESKTOP, overflow: 'auto' }}>
					<ProvincesChart height={500} />
				</Box>
			)
		},
		{
			label: 'Bản đồ',
			content: (
				<Box sx={{ height: isMobile ? LIST_HEIGHTS.MOBILE : LIST_HEIGHTS.DESKTOP, display: 'flex', flexDirection: 'column' }}>
					<ProvincesChoropleth fullHeight />
				</Box>
			)
		}
	];

	return (
		<Box sx={{
			width: '100%',
			height: '100vh',
			display: 'flex',
			flexDirection: 'column',
			overflow: 'hidden',
			p: 0
		}}>
			<PageHeader
				title="Tỉnh/Thành phố (Trước sáp nhập)"
				onRefresh={handleRefresh}
				onAdd={canEdit ? handleCreate : undefined}
				isAdmin={isAdmin}
				loading={isLoading}
			/>

			<PersistentTabs
				pageKey="provinces-original"
				tabs={tabs}
				sx={{
					flex: 1,
					display: 'flex',
					flexDirection: 'column',
					overflow: 'hidden',
					minHeight: 0
				}}
			/>

			<DeleteConfirmDialog
				open={deleteDialog.open}
				onClose={handleCloseDeleteDialog}
				onConfirm={handleDeleteConfirm}
				title="Xóa tỉnh/thành phố"
				message={`Bạn có chắc chắn muốn xóa tỉnh/thành phố "${deleteDialog.province?.tenTinh}"? Hành động này không thể hoàn tác.`}
				isLoading={deleteMutation.isPending}
			/>

			<ProvinceDialog
				open={provinceDialog.open}
				onClose={handleCloseProvinceDialog}
				province={provinceDialog.province}
				mode={provinceDialog.mode}
				merged={false}
			/>
		</Box>
	);
}

export { ProvincesOriginalContent };
